import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import disableRemoteLogin from 'actions/login/disableRemoteLogin.action';
import enableRemoteLogin from 'actions/login/enableRemoteLogin.action';
import getPin from 'actions/pin/getPin.action';
import setPin from 'actions/pin/setPin.action';
import togglePin from 'actions/pin/togglePin.action';
import fetchStylist from 'actions/stylist/fetchStylist.action';
import fetchStylistsBySalon from 'actions/stylist/fetchStylistsBySalon.action';
import toggleCalendarSync from 'actions/stylist/toggleCalendarSync.action';
import { feStripeFoldToggle } from 'actions/ui.actions';
import icoClose from 'assets/images/close-menu_white.svg';
import emailPinIco from 'assets/images/email-pin.svg';
import STRIPE_LOGO from 'assets/images/logos/stripe-logo-blue.png';
import resetPinIco from 'assets/images/reset-pin.svg';
import validateEmail from 'bl/validation/validateEmail.bl';
import DialogMessage from 'components/dialog/DialogMessage.component';
import SalonModal from 'components/dialog/SalonModal.component';
import ButtonModal from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import AlertPanel from 'components/generic/AlertPanel.component';
import Badge from 'components/generic/Badge.component';
import ExternalLink from 'components/generic/ExternalLink.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import ResponsiveBox from 'components/generic/ResponsiveBox.component';
import Space from 'components/generic/Space.component';
import Tooltip from 'components/generic/Tooltip.component';
import TooltipForReceptionist from 'components/TooltipForReceptionist';
import CardWaitlist from 'components/waitlist/CardWaitlist.component';
import PhoneInput from 'components/wrapped/PhoneInput.component';
import FLAG from 'config/flag.singleton';
import { USE_CALLSYNC_BUTTON } from 'config/use.conf';
import PERM from 'constants/core/perm.const';
import IN from 'constants/in';
import LINK from 'constants/link.const';
import { NO } from 'constants/no';
import DISPLAY_POLICY_TEXT from 'constants/pos/stripePolicyDisplayedText.const';
import TRANSFER_POLICY_TEXT_LOWER from 'constants/pos/stripeTransferPolicyTextLower.const';
import USER_ROLE from 'constants/role';
import S from 'constants/string.const';
import { useHelpLinks } from 'hooks/useHelpLinks.hook';
import { useNavFor } from 'hooks/useNav.hook';
import { useStaffStripeSelfManagement } from 'hooks/useStaffStripeSelfManagement.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import selectOnboardingStylistsMap from 'selectors/selectOnboardingStylistsMap.selector';
import getAny from 'utils/accessors/getAny.util';
import getString from 'utils/accessors/getString.util';
import findById from 'utils/arrays/findById.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import fromPermissionToPretty from 'utils/converters/fromPermissionToPretty.util';
import determineFirstNonNil from 'utils/determineFirstNonNil';
import { isEnvMobile } from 'utils/env/determine.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import { isValidPhone } from 'utils/predicates/isValid.util';
import isEmptyString from 'utils/predicates/string/isEmptyString.util';
import { getActiveStylists } from 'utils/salons/getActiveStylists.util';
import equalStrings from 'utils/strings/equalStrings.util';

import { StaffStripeBlockedModal } from 'common/StaffStripeBlockedModal';
import InputTypeDropdown from 'onboarding/common/forms/dropdown/dropdown/InputTypeDropdown';
import InputField from 'onboarding/common/InputField';
import EmailTooltipText from 'onboarding/stylists/AddStylist/EmailTooltipText.component';
import PermissionLevelTooltip from 'onboarding/stylists/AddStylist/PermissionLevelTooltip.component';
import { emailPin as emailPinAction, resetPin as resetPinAction } from 'salon/actions';
import Button from 'settings/page-elements/Button';
import Checkbox from 'settings/page-elements/dropdown/Checkbox';

import CN from './AddStylist.module.scss';

const L = logger('AddStylist');

const EMPTY_ARRAY = [];
const FLAG_DISPLAY_ELM = false;
const EMAIL_INPUT_ID = 'DropdownEmail';

const getTransferPolicyText = (transferPolicy) => DISPLAY_POLICY_TEXT[transferPolicy] || 'composite.stripePolicyDisplayedText.unknownPolicy';

const stateUpdateForRoles = ({ roles, role, roleId, servesAsWaitlist, isSelfEmployed, onRoleChange }) => {
  if (!roles) {
    return {};
  }

  const update = {};

  if (role) {
    update.selectedRole = role;
  } else if (roles.length > 0) {
    // eslint-disable-next-line prefer-destructuring
    update.selectedRole = roles[0];
    if (roleId) {
      for (let i = 0; i < roles.length; i += 1) {
        if (roles[i].id === roleId) {
          update.selectedRole = roles[i];
        }
      }
    }
    onRoleChange(update.selectedRole.id);
  }

  if (servesAsWaitlist) {
    update.serves_as_waitlist = true;
  }

  if (isSelfEmployed) {
    update.selfEmployed = true;
  }

  return update;
};

const determinePinForStylistId = ({ stylistId, pinCodes }) => getAny(undefined, [stylistId, 'pin'], pinCodes);
const determineStylistIdFromProps = (ownStylistId, stylist) => determineFirstNonNil(ownStylistId, getAny(null, 'id', stylist));
const determineEmail = (stylist) => getString('email', stylist);

const determinePermission = ({ email, state, stylist, hasEmailError, emailsWithLogin }) => {
  const { permissionName: permissionFromState } = state;
  const { user_id: userId, permission_level: permissionFromProps, email: emailFromProps } = stylist || {};
  const hasEmail = !!fromAnyToString(emailFromProps);
  const foundInfoById = findById(userId, emailsWithLogin);
  const foundPermissionById = foundInfoById && foundInfoById.permission_level;

  if (email) {
    // new email has been attached, if userId exists for it, disregard the standard logic
    const foundInfoByEmail = emailsWithLogin.find((info) => info.email === email);
    if (foundInfoByEmail && foundInfoByEmail.id) {
      const result = foundInfoByEmail.permission_level;
      return L.debug('determinePermission()', 'result:', result);
    }
  }

  const result =
    permissionFromState ||
    (permissionFromProps === USER_ROLE.noAccess ? S.empty : permissionFromProps) ||
    foundPermissionById ||
    (!hasEmailError && hasEmail && USER_ROLE.manager) ||
    USER_ROLE.noAccess;

  return L.debug('determinePermission()', 'result:', result);
};

const TooltipCalSyncText = ({ t }) => (
  <div className={CN.tooltipContainer}>
    <div className={CN.tooltipTitle}>{t('stylistScreen.addStylist.pinCodeAcces.tooltipCalSync.title')}</div>
    <div className={CN.bullet}>{t('stylistScreen.addStylist.pinCodeAcces.tooltipCalSync.bullet1')}</div>
    <div className={CN.bullet}>{t('stylistScreen.addStylist.pinCodeAcces.tooltipCalSync.bullet2')}</div>
  </div>
);

const TooltipRemoteLoginText = ({ t }) => (
  <div className={CN.tooltipContainer}>
    <div className={CN.tooltipTitle}>{t('stylistScreen.addStylist.pinCodeAcces.tooltipRemoteLogin.title')}</div>
    <div className={CN.bullet}>{t('stylistScreen.addStylist.pinCodeAcces.tooltipRemoteLogin.bullet1')}</div>
  </div>
);

export const AddStylist = ({
  roles = [],
  roleId: roleIdProp = null,
  validateName,
  onNameChange,
  isAddingNewUser,
  saveNewBtnName,
  onEmailChange,
  validateNameLength,
  hasEmailError,
  isSaved,
  validateEmail: onValidateEmail,
  isPostingLoader,
  onPhoneChange,
  onSelfEmployedChange,
  onWaitlistChange,
  onPermissionLevelChange,
  onRoleChange,
  onAddNewStylistBtnClick,
  stylistId: ownStylistId,
  stylist: ownStylist,
}) => {
  const dispatch = useDispatch();
  const nav = useNavFor('AddStylist');
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { setupWaitlistHelpLink } = useHelpLinks();
  const { blockedMessage: staffStripeBlockedMessage, isBlocked: isStaffStripeBlocked, clearBlocked: clearStaffStripeBlocked, blockIfNotOwn: blockIfNotOwnStaffStripe } = useStaffStripeSelfManagement();

  // Redux state
  const reduxStylists = useSelector((state) => state.onboarding.stylists.stylists);
  const editedStylist = ownStylist;
  const stylistFromMap = useSelector((state) => {
    const stylistId = determineStylistIdFromProps(ownStylistId, ownStylist);
    return selectOnboardingStylistsMap(state)[stylistId];
  });
  const stylist = stylistFromMap || ownStylist;
  const emailsWithLogin = useSelector((state) => state.salon.stylistList.emailsWithLogin) || EMPTY_ARRAY;
  const salonId = useSelector((state) => state.currentSalon.id);
  const pinCodes = useSelector((state) => state.salon.userPermission.pinCodes);

  // Refs
  const RefJob = useRef(null);
  const RefEmployment = useRef(null);
  const RefPermission = useRef(null);
  const RefEmail = useRef(null);
  const RefDropdowns = useRef(null);
  const pinInputRef = useRef(null);
  const remoteInputRef = useRef(null);
  const pinFormRef = useRef(null);
  const inputEmailRef = useRef(null);

  // Compute initial state from props
  const {
    has_column: initialHasColumn,
    is_self_employed: isSelfEmployed,
    phone: initialPhone,
    serves_as_waitlist: servesAsWaitlistProp,
    email: initialEmail,
    role: initialRole,
    permission_level: initialPermissionLevel,
    has_cal_sync: initialHasCalSync,
    role_id: initialRoleId,
  } = ownStylist || {};

  const initialRoleUpdate = stateUpdateForRoles({
    roles,
    role: initialRole,
    roleId: roleIdProp || initialRoleId,
    servesAsWaitlist: servesAsWaitlistProp,
    isSelfEmployed,
    onRoleChange,
  });

  // State
  const [hasColumn] = useState(!!initialHasColumn);
  const [selectedRole, setSelectedRole] = useState(initialRoleUpdate.selectedRole || null);
  const [roleDropdownModal, setRoleDropdownModal] = useState(false);
  const [serves_as_waitlist, setServesAsWaitlist] = useState(initialRoleUpdate.serves_as_waitlist || false);
  const [selfEmployed, setSelfEmployedState] = useState(initialRoleUpdate.selfEmployed || false);
  const [employmentDropdown, setEmploymentDropdown] = useState(false);
  const [checkedCalSync, setCheckedCalSync] = useState(initialHasCalSync || false);
  const [disableLoginWithEmailModal, setDisableLoginWithEmailModal] = useState(false);
  const [enableLoginWithEmailModal, setEnableLoginWithEmailModal] = useState(false);
  const [missingEmailModal, setMissingEmailModal] = useState(false);
  const [enablePinModal, setEnablePinModal] = useState(false);
  const [emailSentModal, setEmailSentModal] = useState(false);
  const [type, setType] = useState('password');
  const [resetPinModal, setResetPinModal] = useState(false);
  const [newPinModal, setNewPinModal] = useState(false);
  const [isExternal, setIsExternal] = useState(false);
  const [isInternal, setIsInternal] = useState(false);
  const [emailDropdown, setEmailDropdown] = useState(false);
  const [permissionDropdown, setPermissionDropdown] = useState(false);
  const [tooltip, setTooltip] = useState(false);
  const [permissionItems] = useState([
    { id: 0, name: 'common.permissionRoles.receptionist' },
    { id: 1, name: 'common.permissionRoles.manager' },
  ]);
  const [permissionName, setPermissionName] = useState(initialPermissionLevel || '');
  const [stylistEmail, setStylistEmail] = useState(initialEmail || '');
  const [phone, setPhone] = useState(initialPhone || '');
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loadingPin, setLoadingPin] = useState(false);
  const [emailInviteSentModal, setEmailInviteSentModal] = useState(false);
  const [tooltipForExistingEmail, setTooltipForExistingEmail] = useState(false);
  const [alertSuccess, setAlertSuccess] = useState(false);
  const [emailSentPinCodeModal, setEmailSentPinCodeModal] = useState(false);
  const [inputEmailValue, setInputEmailValue] = useState('');
  const [emailHasError, setEmailHasError] = useState(false);
  const [emailSentText1, setEmailSentText1] = useState('');
  const [disableCalSyncModal, setDisableCalSyncModal] = useState(false);
  const [renderNoActiveStylistModal, setRenderNoActiveStylistModal] = useState(false);
  const [renderExistingEmailsModal, setRenderExistingEmailsModal] = useState(false);
  const [confirmEmail, setConfirmEmailState] = useState('');
  const [emailError, setEmailError] = useState('');
  // Derived values for determineStylistId / determineEmail, using latest stylist from redux
  const currentStylistId = determineStylistIdFromProps(ownStylistId, stylist);
  const currentEmail = determineEmail(stylist);

  // componentDidMount
  useEffect(() => {
    const handleClickOutsideDropdown = (e) => {
      if (RefJob.current && !RefJob.current.contains(e.target)) {
        setRoleDropdownModal(false);
      }
      if (RefEmployment.current && !RefEmployment.current.contains(e.target)) {
        setEmploymentDropdown(false);
      }
      if (RefPermission.current && !RefPermission.current.contains(e.target)) {
        setPermissionDropdown(false);
      }
      if (RefEmail.current && !RefEmail.current.contains(e.target)) {
        setEmailDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideDropdown);

    const email = currentEmail;
    if (pinInputRef.current) {
      pinInputRef.current.disabled = !email;
    }
    if (remoteInputRef.current) {
      remoteInputRef.current.disabled = !email;
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideDropdown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Replaces UNSAFE_componentWillReceiveProps for email/permission sync
  const prevStylistRef = useRef(stylist);
  useEffect(() => {
    const oldStylist = prevStylistRef.current;
    const newStylist = stylist;
    prevStylistRef.current = stylist;

    const { has_remote_login: oldHasRemoteLogin } = oldStylist || {};
    const { has_remote_login: newHasRemoteLogin, email: newEmail, permission_level: permissionFromProps } = newStylist || {};

    L.debug('useEffect[stylist]', newStylist, '<-', oldStylist);

    if (pinInputRef.current && newEmail) {
      pinInputRef.current.disabled = false;
    }
    if (remoteInputRef.current && newEmail) {
      remoteInputRef.current.disabled = false;
    }

    if (newHasRemoteLogin && !oldHasRemoteLogin) {
      // have to show this modal only stylist has_remote_login is false
    }

    if (newEmail && !stylistEmail) {
      setStylistEmail(newEmail);
    }

    if (permissionFromProps && !permissionName) {
      setPermissionName(permissionFromProps);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stylist]);

  // Handlers
  const onRoleClick = useCallback(() => {
    setRoleDropdownModal((prev) => !prev);
  }, []);

  const onRoleDropdownClose = useCallback(() => {
    setRoleDropdownModal(false);
  }, []);

  const onRoleSelect = useCallback(
    (item) => {
      if (roles) {
        for (let i = 0; i < roles.length; i += 1) {
          if (roles[i].id === item.id) {
            onRoleChange(roles[i].id);
            setSelectedRole(roles[i]);
            setRoleDropdownModal(false);
            break;
          }
        }
      }
    },
    [roles, onRoleChange],
  );

  const onEmploymentTypeSelect = useCallback(() => {
    setEmploymentDropdown((prev) => !prev);
  }, []);

  const onClose = useCallback(() => {
    setEnablePinModal(false);
    setDisableLoginWithEmailModal(false);
    setEnableLoginWithEmailModal(false);
    setResetPinModal(false);
    setNewPinModal(false);
    setEmailSentModal(false);
    setEmailInviteSentModal(false);
    setEmailSentPinCodeModal(false);
    setInputEmailValue('');
    setDisableCalSyncModal(false);
  }, []);

  const onResetPin = useCallback(() => {
    setResetPinModal(true);
  }, []);

  const onResetButtonClick = useCallback(() => {
    setResetPinModal(false);

    if (stylist && stylist.id && typeof stylist.id === 'number') {
      dispatch(resetPinAction(salonId, stylist.id));
    }

    const interval = setInterval(() => {
      if (pinCodes && pinCodes[stylist.id] && pinCodes[stylist.id].pin) {
        clearInterval(interval);
        setNewPinModal(true);
      }
    }, 500);
  }, [stylist, salonId, pinCodes, dispatch]);

  const onCheckboxEmailClick = useCallback(() => {
    const { has_remote_login: hasRemoteLogin, email } = stylist || {};

    if (!hasRemoteLogin && !email) return setMissingEmailModal(true);
    if (!hasRemoteLogin) return setDisableLoginWithEmailModal(true);
    return setEnableLoginWithEmailModal(true);
  }, [stylist]);

  const enableCalSync = useCallback(() => {
    const { id } = stylist || {};

    L.unignore('enableCalSync()', 'toggleCalendarSync:', dispatch(toggleCalendarSync({ stylistId: id, salonId, syncEnabled: true })));

    setEmailSentText1(t('stylistScreen.addStylist.emailSentModal.description2'));
    setEmailSentModal(true);
    setCheckedCalSync(true);
  }, [stylist, salonId, dispatch, t]);

  const onCheckboxCalSync = useCallback(() => {
    const { email } = stylist || {};

    if (!email) return setMissingEmailModal(true);
    return checkedCalSync ? setDisableCalSyncModal(true) : enableCalSync();
  }, [stylist, checkedCalSync, enableCalSync]);

  const onSalonPhoneNumberChange = useCallback(
    (value) => {
      onPhoneChange(value.ev);
      setPhone(value.ev);
    },
    [onPhoneChange],
  );

  const onPinSectionClick = useCallback(() => {
    L.debug('onPinSectionClick()');
    setTooltip(true);
    setTimeout(() => setTooltip(false), 5000);
  }, []);

  const setSelfEmployed = useCallback(
    (tf) => {
      setSelfEmployedState(tf);
      onSelfEmployedChange(tf);
      setEmploymentDropdown(false);
    },
    [onSelfEmployedChange],
  );

  const setInternalFn = useCallback(() => {
    setIsExternal(false);
    setIsInternal(true);
  }, []);

  const setExternalFn = useCallback(() => {
    setIsExternal(true);
    setIsInternal(false);
  }, []);

  const setWaitList = useCallback(() => {
    const isWaitlist = stylist && stylist.serves_as_waitlist;
    const activeStylistsWithoutWaitlist = getActiveStylists(reduxStylists);

    if (activeStylistsWithoutWaitlist.length === 1 && !isWaitlist) {
      setRenderNoActiveStylistModal(true);
    } else {
      setServesAsWaitlist((prev) => {
        const next = !prev;
        onWaitlistChange(next);
        return next;
      });
    }
  }, [stylist, reduxStylists, onWaitlistChange]);

  const closeDeleteStylistModal = useCallback(() => {
    setRenderNoActiveStylistModal(false);
  }, []);

  const selectPermission = useCallback(
    (name) => {
      setPermissionDropdown(false);
      setPermissionName(name);
      onPermissionLevelChange(name.toLowerCase());
    },
    [onPermissionLevelChange],
  );

  const togglePinOnOff = useCallback(async () => {
    // NOTE: BE sends null if pin was never set, false if it was set then disabled, true if set and enabled
    const hasPin = stylist && stylist.has_pin_login;
    L.debug('togglePinOnOff()', hasPin);

    if (!enablePinModal) {
      setEnablePinModal(true);
      return;
    }

    const stylistId = currentStylistId;
    if (!stylistId || typeof stylistId !== 'number') {
      return;
    }

    setEnablePinModal(false);
    setLoadingPin(true);
    const options = { salonId, stylistId, pinStatus: !hasPin };
    // if pin was not set before (null status) need to call setPin() action, otherwise togglePin()
    const result = await dispatch(hasPin === null ? setPin(options) : togglePin(options));
    setLoadingPin(false);

    // TODO: provide user notification for success or failure here, check the result for more info
    if (fromAnyToString(result.type).endsWith('_FAILURE')) {
      L.warn('togglePinOnOff()', 'Setting pin failed', result.errorMessage);
    } else {
      L.debug('togglePinOnOff()', 'Setting pin succeeded with result:', result);
    }

    try {
      const result = await dispatch(fetchStylist({ salonId, stylistId }));
      L.debug('togglePinOnOff()', 'Fetching stylist succeeded with result:', result);
    } catch (e) {
      L.warn('togglePinOnOff()', 'Fetching stylist failed.', e);
    }

    // not interested if/how this one finishes at this moment, so no await for it
    L.unignore('togglePinOnOff()', dispatch(fetchStylistsBySalon(salonId)));
  }, [stylist, enablePinModal, currentStylistId, salonId, dispatch]);

  const handlePinCheckboxClick = useCallback(() => {
    const email = currentEmail;
    L.debug('handlePinCheckboxClick()', { email });
    if (!email) {
      setTooltip(true);
      setTimeout(() => setTooltip(false), 5000);
      return;
    }

    L.unignore('handlePinCheckboxClick()', togglePinOnOff());
  }, [currentEmail, togglePinOnOff]);

  const handlePermissionClick = useCallback(() => {
    setPermissionDropdown((prev) => !prev);
  }, []);

  const handleClickAddNewEmail = useCallback(() => {
    const emailInput = document.getElementById(EMAIL_INPUT_ID);

    if (emailInput) {
      emailInput.focus();
      setStylistEmail('');
    }
    setEmailDropdown((prev) => !prev);
  }, []);

  const toggleEmailDropdown = useCallback(
    (inputEmail) => {
      L.debug('toggleEmailDropdown()', { isAddingNewUser, emailDropdown, inputEmail });

      const newStylistEmail = fromAnyToString(inputEmail);
      if (newStylistEmail) {
        setStylistEmail(newStylistEmail);
      }
      setEmailDropdown((prev) => !prev);

      const emailComponent = RefDropdowns.current;
      if (!emailComponent) {
        return void L.warn('toggleEmailDropdown()', 'Invalid emailComponent:', emailComponent);
      }

      const effectivePermission = determinePermission({
        email: newStylistEmail,
        state: { permissionName },
        stylist,
        hasEmailError,
        emailsWithLogin,
      });

      if (permissionName !== effectivePermission) {
        // attaching new email should pre-set the permission field with the BE preferred value
        L.info('toggleEmailDropdown()', 'Changing permission to', effectivePermission, 'from', permissionName);
        // we want to set BE preferred value when add new stylist with add new email
        selectPermission(effectivePermission);
      }

      const email = currentEmail;
      const stylistId = currentStylistId;
      if (stylistId && stylistId !== NO.id && email) {
        return void setTooltipForExistingEmail(true);
      }

      L.debug('toggleEmailDropdown()', newStylistEmail, email);

      if (!email || !pinInputRef.current || pinInputRef.current.disabled) {
        return void setTooltipForExistingEmail(false);
      }

      const errorMessage = onValidateEmail(email);
      if (isAddingNewUser && email && errorMessage) {
        return void setTooltipForExistingEmail(false);
      }
      return void setEmailDropdown(true);
    },
    [isAddingNewUser, emailDropdown, permissionName, stylist, hasEmailError, emailsWithLogin, currentEmail, currentStylistId, onValidateEmail, selectPermission],
  );

  const sentEmailPin = useCallback(() => {
    setEmailSentPinCodeModal(true);
    if (stylist && stylist.id) {
      dispatch(emailPinAction(salonId, stylist.id));
    }
  }, [stylist, salonId, dispatch]);

  const toggleEmailLogin = useCallback(async () => {
    const stylistId = currentStylistId;

    if (stylistId) {
      if (enableLoginWithEmailModal) {
        const disableResult = await dispatch(disableRemoteLogin({ salonId, stylistId }));
        L.warn('toggleEmailLogin()', { disableResult });
      } else if (disableLoginWithEmailModal) {
        const enableResult = await dispatch(enableRemoteLogin({ salonId, stylistId }));

        const inviteEmail = enableResult.response.invite_email;

        if (inviteEmail === false) {
          setEmailInviteSentModal(true);
        } else if (stylist && stylist.has_remote_login) {
          setEmailSentModal(false);
        } else {
          setEmailSentModal(true);
          setEmailSentText1(t('stylistScreen.addStylist.emailSentModal.description1'));
        }
      }
    } else {
      L.warn('toggleEmailLogin()', 'Invalid stylistId:', stylistId);
    }

    setDisableLoginWithEmailModal(false);
    setEnableLoginWithEmailModal(false);

    try {
      setLoadingEmail(true);
      const fetchStylistResult = await dispatch(fetchStylist({ salonId, stylistId }));
      L.warn('toggleEmailLogin()', 'fetchStylistResult:', fetchStylistResult);

      L.unignore('toggleEmailLogin()', dispatch(fetchStylistsBySalon(salonId)));
      // NOTE: no catch here because handleToggleEmail will
    } finally {
      setLoadingEmail(false);
    }
  }, [currentStylistId, enableLoginWithEmailModal, disableLoginWithEmailModal, salonId, stylist, dispatch, t]);

  const handleCalSyncEmailChange = useCallback((value) => {
    setInputEmailValue(value.target.value);
  }, []);

  // FIXME: emailHasError should be boolean, not contain the message
  const validateCalSyncEmail = useCallback(
    (value) => {
      setEmailHasError(validateEmail({ value }) ? '' : t('common.errors.validation.notValidEmail'));
    },
    [t],
  );

  const handleSendCalSyncEmail = useCallback(() => {
    setEmailSentText1(t('stylistScreen.addStylist.emailSentModal.description2'));
    setEmailSentModal(true);
  }, [t]);

  const disableCalSync = useCallback(() => {
    const { id } = stylist || {};

    L.unignore('disableCalSync()', 'toggleCalendarSync:', dispatch(toggleCalendarSync({ stylistId: id, salonId, syncEnabled: false })));

    setDisableCalSyncModal(false);
    setCheckedCalSync(false);
  }, [stylist, salonId, dispatch]);

  const notifySuccess = useCallback(() => {
    setAlertSuccess(true);
    setTimeout(() => setAlertSuccess(false), 3000);
  }, []);

  const save = useCallback(async () => {
    const permission = permissionName;
    const stylistId = currentStylistId;

    L.debug('save()', { stylistId, hasColumn: isInternal, permission });

    try {
      // no need of fetchStylistsBySalon here, the parent component will handle that
      const saveResult = await onAddNewStylistBtnClick({ stylistId: NO.id, permission, hasColumn: isInternal });
      L.debug('save()', 'saveResult:', saveResult);

      // TODO: add different messages for success and failure based on saveResult
      // NOTE: in case of adding a new stylist, the accurate ID can be determined from the response itself
      const actualStylistId = stylistId && stylistId > 0 ? stylistId : (saveResult.response || {}).id;

      if (actualStylistId && actualStylistId > 0) {
        const fetchStylistResult = await dispatch(fetchStylist({ salonId, stylistId: actualStylistId }));
        L.debug('save()', 'fetchStylistResult:', fetchStylistResult);
      }
    } finally {
      notifySuccess();
    }
  }, [permissionName, currentStylistId, isInternal, onAddNewStylistBtnClick, salonId, dispatch, notifySuccess]);

  const validateEmailMatch = useCallback(() => {
    if (editedStylist && editedStylist.email && confirmEmail) {
      if (editedStylist.email !== confirmEmail) {
        setEmailError(t('stylistScreen.addStylist.emailError1'));
      } else {
        setEmailError('');
      }
    } else if (editedStylist && editedStylist.email && !confirmEmail) {
      setEmailError(t('stylistScreen.addStylist.emailError2'));
    } else {
      setEmailError('');
    }
  }, [editedStylist, confirmEmail, t]);

  const onConfirmEmailChange = useCallback(
    (value) => {
      setConfirmEmailState(value);
      // Need to validate with the new value; using a microtask to ensure state is updated
      // But since we have access to the value directly, we validate inline
      if (editedStylist && editedStylist.email && value) {
        if (editedStylist.email !== value) {
          setEmailError(t('stylistScreen.addStylist.emailError1'));
        } else {
          setEmailError('');
        }
      } else if (editedStylist && editedStylist.email && !value) {
        setEmailError(t('stylistScreen.addStylist.emailError2'));
      } else {
        setEmailError('');
      }
    },
    [editedStylist, t],
  );

  const handleSave = useCallback(() => {
    const existingEmails = reduxStylists?.map((s) => s?.email) || [];

    if (confirmEmail) {
      const isDuplicate = existingEmails.some((email) => equalStrings(email, confirmEmail));

      if (isDuplicate) {
        setRenderExistingEmailsModal(true);
        return;
      }
    }

    L.unignore('handleSave()', save());
  }, [reduxStylists, confirmEmail, save]);

  const handleClickStripe = useCallback(() => {
    if (blockIfNotOwnStaffStripe(currentStylistId, editedStylist?.user_name)) return;

    dispatch(feStripeFoldToggle());
    nav.push({
      pathname: linkTo({ pattern: LINK.salonOnline, params: { salonId } }),
    });
    // narrow dep: only re-bind when the user name actually changes, not on every
    // unrelated mutation of `editedStylist` during edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockIfNotOwnStaffStripe, currentStylistId, dispatch, editedStylist?.user_name, nav, salonId]);

  const showHidePin = useCallback(async () => {
    const stylistId = currentStylistId;

    if (!stylistId) {
      return;
    }

    // TODO: display error message to user should this fail?
    await dispatch(getPin({ salonId, stylistId }));
    setTimeout(() => setType((prev) => (prev === 'text' ? 'password' : 'text')), 500);
  }, [currentStylistId, salonId, dispatch]);

  const handleTogglePin = useCallback(() => void L.unignore('handleTogglePin()', togglePinOnOff()), [togglePinOnOff]);
  const handleToggleEmail = useCallback(() => void L.unignore('handleToggleEmail()', toggleEmailLogin()), [toggleEmailLogin]);

  const handleEmailToggle = useCallback(() => void toggleEmailDropdown(), [toggleEmailDropdown]);

  // Render
  const isMobile = isEnvMobile();
  const isAddMode = isAddingNewUser;

  const emailSentText2 = t('stylistScreen.addStylist.emailSentModal.description3');

  const { has_pin_login: hasPinLogin, has_remote_login: hasRemoteLogin, email, id: stylistId, user_id: userId, transfer_policy: transferPolicy } = stylist || {};

  const { user_name: editedStylistUserName } = editedStylist || {};

  // quirky old behavior of having the state of this component exist outside in parents
  const userName = editedStylistUserName || '';
  const pin = determinePinForStylistId({ stylistId, pinCodes });

  // the email value is more current, but if missing, try the one passed down through props
  const effectiveEmail = email || stylistEmail;
  const isEmailChecked = !!hasRemoteLogin;
  const effectivePermission = determinePermission({
    email: false,
    state: { permissionName },
    stylist,
    hasEmailError,
    emailsWithLogin,
  });

  const selectedRoleName = selectedRole && selectedRole.name;
  const selectedRoleId = selectedRole && selectedRole.id;
  const roleItems = roles
    ? roles.map(({ id, name }) => ({
        id,
        name,
        active: selectedRole ? selectedRoleId === id : false,
      }))
    : undefined;

  const isEditMode = !(isAddMode || !stylistId);
  const isEmailFieldsEmpty = !email && !confirmEmail;

  const isUserNameEmpty = isEmptyString(userName) || !userName?.trim();

  const disablePinForm = userId === undefined || isUserNameEmpty || hasEmailError || !stylistId || permissionName === USER_ROLE.noAccess;

  const hasEmailWithoutPermission = !isEmailFieldsEmpty && effectivePermission?.toLowerCase() === USER_ROLE.noAccess;

  const isSaveNewStylistDisabled =
    hasEmailError ||
    isUserNameEmpty ||
    (email && !confirmEmail) || // if only the first email is filled in
    (['manager', 'receptionist'].includes(effectivePermission?.toLowerCase()) && isEmailFieldsEmpty) ||
    hasEmailWithoutPermission;

  const shouldActivateButton = effectivePermission?.toLowerCase() === USER_ROLE.noAccess && isEmailFieldsEmpty && !isUserNameEmpty;

  const isButtonDisabled = isSaveNewStylistDisabled && !shouldActivateButton;

  const showNoEmailMessage = ['manager', 'receptionist'].includes(effectivePermission?.toLowerCase()) && !email && !confirmEmail;
  const showNoPermissionMessage = hasEmailWithoutPermission;
  // both emails are empty and there is a role

  return (
    <div
      data-bem="AddStylist"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      {alertSuccess && isSaved && <div className="alert success animated fadeInDown">{t('stylistScreen.saved')}</div>}

      <StaffStripeBlockedModal isOpen={isStaffStripeBlocked} message={staffStripeBlockedMessage} onClose={clearStaffStripeBlocked} />

      <SalonModal tag="AddStylist__ExistingEmails" isOpen={renderExistingEmailsModal} variant="noFooter">
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <ButtonModal variant="empty" onClick={() => void setRenderExistingEmailsModal(false)}>
              <Icon variant="close" variantInvert />
            </ButtonModal>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <IconStatus variant="alert" width="lg">
            <Icon variant="alert" width="l1" />
          </IconStatus>
          <div className={CN.modalContent}>
            <div className={CN.modalTitle}>{t('stylistScreen.addStylist.existingEmailsModalTitle.title')}</div>
            <div className={CN.modalText}>{t('stylistScreen.addStylist.existingEmailsModalTitle.description')}</div>
          </div>
        </SalonModal.Body>
      </SalonModal>

      <SalonModal tag="AddStylist" isOpen={renderNoActiveStylistModal}>
        <SalonModal.Body>
          <div className={CN.modalImage}>
            <IconStatus variant="alert">
              <Icon variant="alert" />
            </IconStatus>
          </div>
          <DialogMessage>
            <Space>{t('stylistScreen.addStylist.addStylistWarningModal.descriptionPart1')}</Space>
            <ExternalLink link={setupWaitlistHelpLink} variant="inline">
              <span className={CN.link}>{t('stylistScreen.addStylist.addStylistWarningModal.linkLabel')}</span>
            </ExternalLink>
            <Space>{t('stylistScreen.addStylist.addStylistWarningModal.descriptionPart2')}</Space>
          </DialogMessage>
        </SalonModal.Body>
        <SalonModal.Footer>
          <ButtonModal variant="primaryForce" casing="dialog" onClick={closeDeleteStylistModal}>
            {t('common.buttons.close')}
          </ButtonModal>
        </SalonModal.Footer>
      </SalonModal>

      <SalonModal tag="AddStylist__email-sent" isOpen={emailSentModal} variant="noFooter">
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <ButtonModal variant="empty" onClick={onClose}>
              <Icon variant="close" variantInvert />
            </ButtonModal>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant="success">
              <Icon variant="envelope" width="l1" selected />
            </IconStatus>
            <div className={CN.modalTitle}>{t('stylistScreen.addStylist.emailSentModal.title')}</div>
            <div className={CN.modalText}>{emailSentText1}</div>
            <div className={CN.modalText}>{emailSentText2}</div>
          </div>
        </SalonModal.Body>
      </SalonModal>

      <SalonModal tag="AddStylist__email-sent-pin" isOpen={emailSentPinCodeModal || newPinModal} variant="noFooter">
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <ButtonModal variant="empty" onClick={onClose}>
              <Icon variant="close" variantInvert />
            </ButtonModal>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant="success">
              <Icon variant="envelope" width="l1" selected />
            </IconStatus>
            <div className={CN.modalTitle}>{t('stylistScreen.addStylist.emailSentModal.title')}</div>
            <div className={CN.modalText}>{t('stylistScreen.addStylist.emailSentModal.description4')}</div>
            <br />
            <div className={CN.modalText}>{t('stylistScreen.addStylist.emailSentModal.description5')}</div>
          </div>
        </SalonModal.Body>
      </SalonModal>

      <SalonModal tag="AddStylist__email-access-enabled" isOpen={emailInviteSentModal} variant="noFooter">
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <ButtonModal variant="empty" onClick={onClose}>
              <Icon variant="close" variantInvert />
            </ButtonModal>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant="success">
              <Icon variant="envelope" width="l1" selected />
            </IconStatus>
            <div className={CN.modalTitle}>{t('stylistScreen.addStylist.emailAccessEnabledModal.title')}</div>
            <div className={CN.modalText}>{t('stylistScreen.addStylist.emailAccessEnabledModal.description1')}</div>
            <div className={CN.modalText}>{t('stylistScreen.addStylist.emailAccessEnabledModal.description2')}</div>
          </div>
        </SalonModal.Body>
      </SalonModal>

      <SalonModal tag="AddStylist__reset-pin" isOpen={resetPinModal} withFooter>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant="alert" width="lg">
              <Icon variant="alert" width="l1" />
            </IconStatus>
            <div className={CN.modalTitle}>{t('stylistScreen.addStylist.resetPinModal.title')}</div>
            <div className={CN.modalText}>{t('stylistScreen.addStylist.resetPinModal.description', { user: userName })}</div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <div className={CN.modalButtons}>
            <ButtonModal variant="outline" xSpace onClick={onClose}>
              {t('common.buttons.cancelUpp')}
            </ButtonModal>
            <ButtonModal variant="primary" xSpace onClick={onResetButtonClick}>
              {t('stylistScreen.addStylist.resetPinModal.resetButton')}
            </ButtonModal>
          </div>
        </SalonModal.Footer>
      </SalonModal>

      {isPostingLoader && <OverlaySpinner />}

      <SalonModal tag="AddStylist__toggle-pin" isOpen={disableLoginWithEmailModal} withFooter>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant="alert" width="lg">
              <Icon variant="alert" width="l1" />
            </IconStatus>
            <div className={CN.modalTitle}>{t('stylistScreen.addStylist.togglePinModal.title1')}</div>
            <div className={CN.modalText}>{t('stylistScreen.addStylist.togglePinModal.description1')}</div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <div className={CN.modalButtons}>
            <ButtonModal variant="outline" xSpace onClick={onClose}>
              {t('common.buttons.cancelUpp')}
            </ButtonModal>
            <ButtonModal variant="primary" xSpace onClick={handleToggleEmail}>
              {t('stylistScreen.addStylist.togglePinModal.enableEmailLoginButton')}
            </ButtonModal>
          </div>
        </SalonModal.Footer>
      </SalonModal>

      <SalonModal tag="AddStylist__missing-email" isOpen={missingEmailModal} variant="noFooter">
        <SalonModal.Header>
          <div className={CN.closeButtonContainer}>
            <ButtonModal variant="empty" onClick={() => void setMissingEmailModal(false)}>
              <Icon variant="close" variantInvert />
            </ButtonModal>
          </div>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant="alert" width="lg">
              <Icon variant="alert" width="l1" />
            </IconStatus>
            <div className={CN.modalTitle}>{t('stylistScreen.addStylist.missingEmailModal.title')}</div>
            <div className={CN.modalText}>
              {t('stylistScreen.addStylist.missingEmailModal.descriptionPart1')}
              <span className={CN.textBold}>
                <Space>{t('stylistScreen.addStylist.missingEmailModal.descriptionPart2')}</Space>
              </span>
              {t('stylistScreen.addStylist.missingEmailModal.descriptionPart3')}
            </div>
          </div>
        </SalonModal.Body>
      </SalonModal>

      <SalonModal tag="AddStylist__toggle-pin" isOpen={enableLoginWithEmailModal} withFooter>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant="alert" width="lg">
              <Icon variant="alert" width="l1" />
            </IconStatus>
            <div className={CN.modalTitle}>{t('stylistScreen.addStylist.togglePinModal.title2')}</div>
            <div className={CN.modalText}>{t('stylistScreen.addStylist.togglePinModal.description2')}</div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <div className={CN.modalButtons}>
            <ButtonModal variant="outline" xSpace onClick={onClose}>
              {t('common.buttons.cancelUpp')}
            </ButtonModal>
            <ButtonModal variant="primary" xSpace onClick={handleToggleEmail}>
              {t('stylistScreen.addStylist.togglePinModal.disableEmailLoginButton')}
            </ButtonModal>
          </div>
        </SalonModal.Footer>
      </SalonModal>

      <SalonModal tag="AddStylist__toggle-pin" isOpen={enablePinModal} withFooter>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant={hasPinLogin ? 'alert' : 'success'} width="lg">
              <Icon variant={hasPinLogin ? 'alert' : 'confirm'} width="l1" />
            </IconStatus>
            <div className={CN.modalTitle}>{hasPinLogin ? t('stylistScreen.addStylist.enabledPinModal.title1') : t('stylistScreen.addStylist.enabledPinModal.title2')}</div>

            {hasPinLogin ? (
              <div className={CN.modalText}>{t('stylistScreen.addStylist.enabledPinModal.description1')}</div>
            ) : (
              <>
                <div className={CN.modalText}>{t('stylistScreen.addStylist.enabledPinModal.description2')}</div>
                <div className={CN.alertOuterContainer}>
                  <AlertPanel tag="AddStylist__alert" variant="alert">
                    <div className={CN.alertContainer}>
                      <div className={CN.alertTitle}>{t('stylistScreen.important')}</div>
                      <div>{t('stylistScreen.addStylist.enabledPinModal.alertMessage')}</div>
                    </div>
                  </AlertPanel>
                </div>
              </>
            )}
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <div className={CN.modalButtons}>
            <ButtonModal variant="outline" xSpace onClick={onClose}>
              {t('common.buttons.cancelUpp')}
            </ButtonModal>
            <ButtonModal variant="primary" xSpace onClick={handleTogglePin}>
              {hasPinLogin ? t('stylistScreen.addStylist.enabledPinModal.disablePinButton') : t('common.buttons.doneUpp')}
            </ButtonModal>
          </div>
        </SalonModal.Footer>
      </SalonModal>

      <SalonModal tag="AddStylist__disable-calendar-sync" isOpen={disableCalSyncModal} withFooter>
        <SalonModal.Body>
          <div className={CN.modalContent}>
            <IconStatus variant="alert" width="lg">
              <Icon variant="alert" width="l1" />
            </IconStatus>
            <div className={CN.modalTitle}>{t('stylistScreen.addStylist.disableCalendarModal.title')}</div>
            <div className={CN.modalText}>{t('stylistScreen.addStylist.disableCalendarModal.description')}</div>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <div className={CN.modalButtons}>
            <ButtonModal variant="outline" xSpace onClick={onClose}>
              {t('common.buttons.cancelUpp')}
            </ButtonModal>
            <ButtonModal variant="primary" xSpace onClick={disableCalSync}>
              {t('stylistScreen.addStylist.disableCalendarModal.disableButton')}
            </ButtonModal>
          </div>
        </SalonModal.Footer>
      </SalonModal>

      <div data-bem="AddStylist__content" className="content">
        <div id="add-edit-pin-stylist-container">
          <section data-bem="AddStylist__stylist" id="stylist-form">
            <strong className={CN.infoTitle}>{isEditMode ? t('stylistScreen.addStylist.title1') : t('stylistScreen.addStylist.title1')}</strong>

            {/* EDIT EXISTING STYLIST START */}
            {isEditMode ? (
              <div data-bem="AddStylist__body--edit-mode" id="edit-existing-stylist">
                <div
                  className={cn({
                    'cfix t-container relative': true,
                    [CN.inputContainer]: true,
                  })}
                >
                  <InputField
                    data={{
                      id: 'StaffNameInput',
                      label: t('stylistScreen.inputFields.nameLabel'),
                      type: 'text',
                      placeholder: t('stylistScreen.inputFields.namePlaceholder'),
                      inputValue: userName,
                      inputMode: 'text',
                    }}
                    autoFocus
                    onChangeHandler={onNameChange}
                    validateBlur={validateName}
                    validateChange={validateNameLength}
                  />
                  <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.salon} />
                </div>
                {FLAG_DISPLAY_ELM && (
                  <div className="cfix t-container--phone-sfatt  relative">
                    <span className="field-name" style={{ opacity: 0.6, marginTop: '10px', display: 'block' }}>
                      {t('stylistScreen.inputFields.phoneLabel')}
                    </span>

                    <PhoneInput
                      placeholder={t('stylistScreen.inputFields.phonePlaceholder')}
                      value={phone}
                      onChange={(value) => {
                        onSalonPhoneNumberChange(value);
                      }}
                      autoComplete="off"
                    />
                    {!phone ? (
                      <div className="input-prompt-show">
                        <span />
                      </div>
                    ) : (
                      <div className="input-prompt-show">
                        <span>{isValidPhone({ value: phone }) ? '' : t('common.errors.validation.notValidPhoneNumber')}</span>
                      </div>
                    )}
                    <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.salon} />
                  </div>
                )}

                <div data-bem="AddStylist__email-container--edit-mode" className="cfix t-container relative">
                  {tooltipForExistingEmail && (
                    <div className="disable-edit-email-tooltip">
                      <span role="button" tabIndex="0" onClick={() => setTooltipForExistingEmail(false)}>
                        <img src={icoClose} alt="Exit anyway" />
                      </span>
                      <EmailTooltipText />
                    </div>
                  )}
                  <div
                    data-bem="AddStylist__email--edit-mode"
                    className={cn({
                      'dropdown-stylist email-input': true,
                      [CN.inputContainer]: true,
                    })}
                    ref={RefDropdowns}
                  >
                    <div className="relative" ref={inputEmailRef}>
                      <div className="toggle-email-btn" role="button" tabIndex="0" onClick={handleEmailToggle}>
                        <span className="select-button-icon">
                          <span className="ic-sml ic_expand_more_black" />
                        </span>
                      </div>

                      <InputField
                        data={{
                          id: EMAIL_INPUT_ID,
                          label: t('stylistScreen.inputFields.emailLabel'),
                          type: 'email',
                          placeholder: 'name@salon.com',
                          inputValue: fromAnyToString(effectiveEmail).toLocaleLowerCase(),
                          inputMode: 'email',
                        }}
                        onChangeHandler={(email) => onEmailChange({ email, userId: undefined })}
                        validateBlur={onValidateEmail}
                      ></InputField>
                    </div>

                    {emailDropdown && (
                      <div data-bem="AddStylist__email-dropdown-container" className="item-list" ref={RefEmail}>
                        <div className="scrollable-container">
                          {!emailsWithLogin.length && <div className="popup-list-item">{email || ''}</div>}
                          {emailsWithLogin.map(({ id, email }) => (
                            // eslint-disable-next-line jsx-a11y/interactive-supports-focus
                            <div
                              data-bem="AddStylist__email-dropdown-item"
                              key={id}
                              role="button"
                              className="popup-list-item"
                              onClick={() => {
                                onEmailChange({ userId: id, email });
                                toggleEmailDropdown(email);
                              }}
                            >
                              {email}
                            </div>
                          ))}
                        </div>

                        {permissionName === USER_ROLE.noAccess ? (
                          <Tooltip variant="top" content={t('stylistScreen.addStylist.editMode.emailTooltipContent')}>
                            <div className="popup-list-item fixed-in-list">{t('stylistScreen.addStylist.addEmailButton')}</div>
                          </Tooltip>
                        ) : (
                          <div className="popup-list-item fixed-in-list" role="button" tabIndex="0" onClick={handleClickAddNewEmail}>
                            {t('stylistScreen.addStylist.addEmailButton')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.salon} />
                </div>

                <div>
                  {selectedRole && (
                    <>
                      {hasColumn && (
                        <div
                          data-bem="AddStylist__role-container--edit-mode-internal"
                          className={cn({
                            'cfix t-container relative': true,
                            [CN.inputContainer]: true,
                          })}
                        >
                          <div className="dropdown-stylist scrollable-container" ref={RefJob}>
                            <span className="dropdown-name">{t('stylistScreen.inputFields.jobLabel')}</span>
                            <label htmlFor="StaffRoleSelect" className="settings-heading">
                              <button className="utl-full-width select-button" id="StaffRoleSelect" onClick={onRoleClick} style={{ marginTop: 5 }}>
                                <span className="select-button-text">{selectedRole.name}</span>
                                <span className="select-button-icon">
                                  <span className="ic-sml ic_expand_more_black" />
                                </span>
                              </button>
                            </label>
                            <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.roleDropdown} />
                            {roleItems && roleDropdownModal && <InputTypeDropdown items={roleItems} onClose={onRoleDropdownClose} onSelectItem={onRoleSelect} />}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {hasColumn && (
                    <div
                      data-bem="AddStylist__employment-container--edit-mode-internal"
                      className={cn({
                        'cfix t-container relative': true,
                        [CN.inputContainer]: true,
                      })}
                    >
                      <div className="dropdown-stylist">
                        <span className="dropdown-name">{t('stylistScreen.inputFields.employmentLabel')}</span>
                        <label htmlFor="EmploymentType" className="settings-heading">
                          <button className="utl-full-width select-button" onClick={onEmploymentTypeSelect} style={{ marginTop: 5 }}>
                            <span className="select-button-text">{selfEmployed ? t('stylistScreen.addStylist.selfEmployed') : t('stylistScreen.addStylist.employed')}</span>
                            <span className="select-button-icon">
                              <span className="ic-sml ic_expand_more_black" />
                            </span>
                          </button>
                        </label>
                        {employmentDropdown && (
                          <div className="employment-dropdown" ref={RefEmployment}>
                            <div className="wait-list-checkbox">
                              <div className="label-div">
                                <label htmlFor="Employed">
                                  <Checkbox isChecked={!selfEmployed} id="Employed" onCheckboxClick={() => setSelfEmployed(false)} />
                                  <span>{t('stylistScreen.addStylist.employed')}</span>
                                </label>
                              </div>
                            </div>
                            <div className="wait-list-checkbox">
                              <div className="label-div">
                                <label htmlFor="selfImployed">
                                  <Checkbox isChecked={selfEmployed} id="selfImployed" onCheckboxClick={() => setSelfEmployed(true)} />
                                  <span>{t('stylistScreen.addStylist.selfEmployed')}</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.roleDropdown} />
                    </div>
                  )}
                </div>

                <div
                  data-bem="AddStylist__permission-container--edit-mode"
                  className={cn({
                    'cfix t-container relative': true,
                    [CN.inputContainer]: true,
                  })}
                >
                  <div className="dropdown-stylist">
                    <span className="dropdown-name">
                      {t('stylistScreen.inputFields.permissionLabel')}
                      <PermissionLevelTooltip />
                    </span>
                    <label htmlFor="PermissionLevel" className="settings-heading">
                      <button className="utl-full-width select-button" id="PermissionLevel" onClick={handlePermissionClick} style={{ marginTop: 5 }}>
                        <span className="select-button-text">{fromPermissionToPretty(effectivePermission)}</span>

                        <span className="select-button-icon">
                          <span className="ic-sml ic_expand_more_black" />
                        </span>
                      </button>
                    </label>

                    {permissionItems && permissionDropdown && (
                      <div className="item-list" ref={RefPermission}>
                        {permissionItems.map(({ id, name }) => (
                          <div className="popup-list-item" role="button" tabIndex="0" key={id} onClick={() => selectPermission(t(name))}>
                            {t(name)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.roleDropdown} />
                </div>
                {/* WAITLIST CHECKBOX*/}
                <ResponsiveBox.Desktop>
                  {(isInternal || stylistId) && serves_as_waitlist && hasColumn && (
                    <div data-bem="AddStylist__waitlist-toggler">
                      <div className={CN.waitlistTogglerContainer}>
                        <span className={CN.waitlistTogglerLabel}>{t('stylistScreen.addStylist.editMode.waitlistTogglerLabel')}</span>
                        <Toggler onClick={setWaitList} selected={serves_as_waitlist} />
                        <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.inWaitlist} />
                      </div>
                      <div className={CN.waitlistDescription}>{t('stylistScreen.addStylist.editMode.waitlistDescription')}</div>
                    </div>
                  )}
                </ResponsiveBox.Desktop>
              </div>
            ) : (
              isAddMode && (
                <div data-bem="AddStylist__column-option-body" className="staff-type">
                  {/* ADD NEW STYLIST RADIO // EDIT EXISTING STYLIST END */}
                  <span>
                    {t('stylistScreen.addStylist.addMode.descriptionPart1')} <br />
                    {t('stylistScreen.addStylist.addMode.descriptionPart2')}
                  </span>
                  <div role="button" tabIndex={0} id="internal-staff" onClick={setInternalFn} className={isInternal ? 'active' : 'non'}>
                    <span className="fake-check" />
                    {t('stylistScreen.addStylist.addMode.needsColumnCheckbox')}
                  </div>
                  <div role="button" tabIndex={0} id="external-staff" onClick={setExternalFn} className={isExternal ? 'active' : 'none'}>
                    <span className="fake-check" />
                    {t('stylistScreen.addStylist.addMode.accessOnlyCheckbox')}
                  </div>
                </div>
              )
            )}

            {/*  ADD NEW STYLIST */}

            {(isInternal || isExternal) && (
              <div data-bem="AddStylist__body--add-mode">
                <div className={CN.inputContainer}>
                  <InputField
                    data={{
                      id: 'StaffNameInput',
                      label: t('stylistScreen.inputFields.nameLabel'),
                      type: 'text',
                      placeholder: t('stylistScreen.inputFields.namePlaceholder'),
                      inputValue: userName,
                      inputMode: 'text',
                    }}
                    autoFocus
                    onChangeHandler={onNameChange}
                    validateBlur={validateName}
                    validateChange={validateNameLength}
                  />
                  <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.salon} />
                </div>
                {FLAG_DISPLAY_ELM && (
                  <div className={CN.inputContainer}>
                    <span className="field-name" style={{ opacity: 0.6, marginTop: '10px', display: 'block' }}>
                      {t('stylistScreen.inputFields.phoneLabel')}
                    </span>

                    <PhoneInput
                      placeholder={t('stylistScreen.inputFields.phonePlaceholder')}
                      value={phone}
                      onChange={(value) => {
                        onSalonPhoneNumberChange(value);
                      }}
                      autoComplete="off"
                    />
                    {!phone ? (
                      <div className="input-prompt-show">
                        <span />
                      </div>
                    ) : (
                      <div className="input-prompt-show">
                        <span>{phone && isValidPhone({ value: phone }) ? '' : t('common.errors.validation.notValidPhoneNumber')}</span>
                      </div>
                    )}
                    <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.salon} />
                  </div>
                )}
                <div data-bem="AddStylist__email-container--add-mode" className={CN.inputContainer}>
                  {tooltipForExistingEmail && (
                    <div className="disable-edit-email-tooltip">
                      <span role="button" tabIndex="0" onClick={() => setTooltipForExistingEmail(false)}>
                        <img src={icoClose} alt="Exit anyway" />
                      </span>
                      <EmailTooltipText />
                    </div>
                  )}
                  <div data-bem="AddStylist__email--add-mode" className="dropdown-stylist email-input" ref={RefDropdowns}>
                    <div className="toggle-email-btn" role="button" tabIndex="0" onClick={handleEmailToggle}>
                      <span className="select-button-icon">
                        <span className="ic-sml ic_expand_more_black" />
                      </span>
                    </div>
                    <InputField
                      data={{
                        id: EMAIL_INPUT_ID,
                        label: t('stylistScreen.inputFields.emailLabel'),
                        type: 'email',
                        placeholder: 'name@salon.com',
                        inputValue: fromAnyToString(email),
                        inputMode: 'email',
                      }}
                      onChangeHandler={(email) => {
                        onEmailChange({ email, userId: undefined });
                        validateEmailMatch();
                      }}
                      validateBlur={onValidateEmail}
                    />

                    {emailDropdown && (
                      <div className="item-list" ref={RefEmail}>
                        <div className="scrollable-container">
                          {emailsWithLogin.length > 0 &&
                            emailsWithLogin.map(({ id, email }) => (
                              <div
                                key={id}
                                data-id={id}
                                tabIndex={0}
                                role="button"
                                className="popup-list-item"
                                onClick={() => {
                                  onEmailChange({ userId: id, email });
                                  toggleEmailDropdown(email);
                                  setEmailDropdown(false);
                                }}
                              >
                                {email}
                              </div>
                            ))}
                        </div>
                        <div className="popup-list-item fixed-in-list" role="button" tabIndex="0" onClick={handleClickAddNewEmail}>
                          {confirmEmail ? t('stylistScreen.addStylist.editEmail') : t('stylistScreen.addStylist.addEmailButton')}
                        </div>
                      </div>
                    )}
                  </div>
                  <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.salon} />
                </div>
                <div data-bem="AddStylist__email-confirmation-container--add-mode" className={CN.inputContainer}>
                  <InputField
                    data={{
                      label: t('stylistScreen.inputFields.reEnterEmailLabel'),
                      type: 'email',
                      placeholder: 'name@salon.com',
                      inputValue: fromAnyToString(confirmEmail),
                      inputMode: 'email',
                    }}
                    onChangeHandler={onConfirmEmailChange}
                    validateBlur={validateEmailMatch}
                  />
                  {emailError && <span className={CN.error}>{emailError}</span>}
                </div>

                {isInternal && (
                  <div data-bem="AddStylist__role-container--add-mode" className="for-new-int-staff">
                    {selectedRole && (
                      <div
                        className={cn({
                          'cfix t-container relative': true,
                          [CN.inputContainer]: true,
                        })}
                      >
                        <div className="dropdown-stylist scrollable-container" ref={RefJob}>
                          <span className="dropdown-name">{t('stylistScreen.inputFields.jobLabel')}</span>
                          <label htmlFor="StaffRoleSelect" className="settings-heading">
                            <button className="utl-full-width select-button" id="StaffRoleSelect" onClick={onRoleClick} style={{ marginTop: 5 }}>
                              <span className="select-button-text">{selectedRoleName}</span>
                              <span className="select-button-icon">
                                <span className="ic-sml ic_expand_more_black" />
                              </span>
                            </button>
                          </label>
                          <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.roleDropdown} />
                          {roleItems && roleDropdownModal && <InputTypeDropdown items={roleItems} onClose={onRoleDropdownClose} onSelectItem={onRoleSelect} />}
                        </div>
                      </div>
                    )}

                    <div
                      className={cn({
                        'cfix t-container relative': true,
                        [CN.inputContainer]: true,
                      })}
                    >
                      <div className="dropdown-stylist">
                        <span className="dropdown-name">{t('stylistScreen.inputFields.employmentLabel')}</span>
                        <label htmlFor="EmploymentType" className="settings-heading">
                          <button className="utl-full-width select-button" onClick={onEmploymentTypeSelect} style={{ marginTop: 5 }}>
                            <span className="select-button-text">{selfEmployed ? t('stylistScreen.addStylist.selfEmployed') : t('stylistScreen.addStylist.employed')}</span>
                            <span className="select-button-icon">
                              <span className="ic-sml ic_expand_more_black" />
                            </span>
                          </button>
                        </label>
                        {employmentDropdown && (
                          <div className="employment-dropdown" ref={RefEmployment}>
                            <div className="wait-list-checkbox">
                              <div className="label-div">
                                <label htmlFor="Employed">
                                  <Checkbox isChecked={!selfEmployed} id="Employed" onCheckboxClick={() => setSelfEmployed(false)} />
                                  <span>{t('stylistScreen.addStylist.employed')}</span>
                                </label>
                              </div>
                            </div>
                            <div className="wait-list-checkbox">
                              <div className="label-div">
                                <label htmlFor="selfImployed">
                                  <Checkbox isChecked={selfEmployed} id="selfImployed" onCheckboxClick={() => setSelfEmployed(true)} />
                                  <span>{t('stylistScreen.addStylist.selfEmployed')}</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.roleDropdown} />
                    </div>
                  </div>
                )}

                <div
                  data-bem="AddStylist__permission-container--add-mode"
                  className={cn({
                    'cfix t-container relative': true,
                    [CN.inputContainer]: true,
                  })}
                >
                  <div className="dropdown-stylist">
                    <span className="dropdown-name">
                      {t('stylistScreen.inputFields.permissionLabel')}
                      <PermissionLevelTooltip />
                    </span>
                    <label htmlFor="PermissionLevel" className="settings-heading">
                      <button className="utl-full-width select-button" id="PermissionLevel" onClick={handlePermissionClick} style={{ marginTop: 5 }}>
                        <span className="select-button-text">{fromPermissionToPretty(effectivePermission)}</span>

                        <span className="select-button-icon">
                          <span className="ic-sml ic_expand_more_black" />
                        </span>
                      </button>
                    </label>

                    {permissionItems && permissionDropdown && (
                      <div className="item-list" ref={RefPermission}>
                        {permissionItems.map(({ id, name }) => (
                          <div className="popup-list-item" role="button" tabIndex="0" key={id} onClick={() => selectPermission(t(name))}>
                            {t(name)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.roleDropdown} />
                </div>

                <div data-bem="AddStylist__save-container" className="sticky-div-on-scroll-add-new-stylist">
                  {showNoEmailMessage && (
                    <p className={CN.error}>
                      {t('stylistScreen.addStylist.noEmailMessagePart1')} <br />
                      {t('stylistScreen.addStylist.noEmailMessagePart2')}
                    </p>
                  )}
                  {showNoPermissionMessage && <p className={CN.error}>{t('stylistScreen.addStylist.noPermissionMessage')}</p>}
                  <Button buttonName={saveNewBtnName} onClickButton={handleSave} className="btn-add-new-stylist" disabled={isButtonDisabled} />
                </div>
              </div>
            )}
          </section>
          <div>
            {/*  PIN VIEW */}
            {(isInternal || isExternal || stylistId) && (
              <div data-bem="AddStylist__pin-and-stripe-container" className={CN.leftContainer}>
                <section data-bem="AddStylist__pin-body" id="pin-form" ref={pinFormRef}>
                  {fromAnyToBool(tooltip) && (
                    <div data-bem="AddStylist__pin-tooltip" className="disable-edit-tooltip">
                      {t('stylistScreen.addStylist.pinCodeAcces.tooltip.textPart1')}
                      <strong> {t('stylistScreen.addStylist.pinCodeAcces.tooltip.textPart2')} </strong>
                      {t('stylistScreen.addStylist.pinCodeAcces.tooltip.textPart3')} <strong>{t('stylistScreen.addStylist.pinCodeAcces.tooltip.textPart4')}</strong>{' '}
                      {t('stylistScreen.addStylist.pinCodeAcces.tooltip.textPart5')}
                    </div>
                  )}

                  {disablePinForm && <div id="disable-pin-form-overlay" role="button" onClick={onPinSectionClick} tabIndex={0} />}

                  <div className={disablePinForm ? 'grayout-elements' : ''}>
                    <strong className={CN.infoTitle}>{t('stylistScreen.addStylist.pinCodeAcces.title')}</strong>
                    <div data-bem="AddStylist__pin-input-container" className="cfix t-container relative">
                      <div className={CN.editStylistTogglerContainer}>
                        <div className={CN.editStylistTogglerLabel}>
                          {t('stylistScreen.addStylist.pinCodeAcces.pinLoginCheckbox')}
                          <div
                            className={cn({
                              [CN.pinLoginStatus]: true,
                              [CN.disabled]: !hasPinLogin,
                            })}
                          >
                            {hasPinLogin ? t('common.enabled').toUpperCase() : t('common.disabled').toUpperCase()}
                          </div>
                        </div>

                        <Toggler onClick={handlePinCheckboxClick} selected={hasPinLogin} hideLabel />
                        <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.roleDropdown} />
                        {loadingPin && (
                          <div className="spinner-container">
                            <div className="load-spinner" />
                          </div>
                        )}
                      </div>
                    </div>

                    {hasPinLogin && (
                      <div className={hasPinLogin ? '' : 'disable-html'}>
                        <div className="cfix t-container relative">
                          <div className="form-horizontal">
                            <label htmlFor="PinCode" className="pin-code-input">
                              <span className="field-name col-md-5" style={{ display: 'block' }}>
                                {t('stylistScreen.addStylist.pinCodeAcces.pin')}
                              </span>
                              <input id="PinCode" type={type} placeholder="●●●●" value={pin} readOnly />
                              {FLAG.pinCode && (
                                <div className="show-hide-pin" role="button" tabIndex="0" onClick={() => void showHidePin()}>
                                  <span className={type === 'password' ? 'Hide' : 'Show'}>{type === 'password' ? t('common.show') : t('common.hide')}</span>
                                </div>
                              )}
                            </label>
                          </div>
                          <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.roleDropdown} />
                        </div>
                        <div className="cfix t-container relative">
                          <div className="pin-btn-container">
                            <div className="relative">
                              <button onClick={onResetPin} className="btn btn-outline" id="reset-pin-btn">
                                <img src={resetPinIco} alt={t('stylistScreen.addStylist.pinCodeAcces.resetPinButton')} style={{ marginRight: '4px' }} />
                                {t('stylistScreen.addStylist.pinCodeAcces.resetPinButton')}
                              </button>
                              <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.roleDropdown} />
                            </div>
                            <div className="relative">
                              <button onClick={sentEmailPin} className="btn btn-outline" id="email-pin-btn">
                                <img src={emailPinIco} alt="" />
                                {t('stylistScreen.addStylist.pinCodeAcces.emailPin')}
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div id="EmailLoginAndCalSync">
                      <div className="cfix t-container ">
                        <strong className="info-title">{t('stylistScreen.addStylist.pinCodeAcces.remoteUserAccess')}</strong>
                      </div>
                      <div className="cfix t-container relative">
                        <div data-bem="AddStylist__remote-login-with-email" className={CN.editStylistTogglerContainer}>
                          <span className={CN.editStylistTogglerLabel}>
                            <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.roleDropdown} />
                            <Tooltip variant={isMobile ? 'slickRightToBottom' : 'leftToBottom'} width={isMobile ? 'lg' : 'max'} content={<TooltipRemoteLoginText t={t} />}>
                              <div className={CN.iconInfo}>
                                <Icon variant="info" width="md" />
                              </div>
                            </Tooltip>
                            {t('stylistScreen.addStylist.pinCodeAcces.remoteLoginEmail')}
                          </span>
                          <Toggler onClick={onCheckboxEmailClick} selected={isEmailChecked} hideLabel />
                          <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.inWaitlist} />
                        </div>
                        {loadingEmail && (
                          <div className="spinner-container">
                            <div className="load-spinner" />
                          </div>
                        )}
                      </div>
                      {hasColumn && (
                        <div className="cfix t-container cal-sync-container">
                          <div data-bem="AddStylist__cal-sync" className={CN.editStylistTogglerContainer}>
                            <div className={CN.editStylistTogglerLabel}>
                              <Tooltip variant={isMobile ? 'slickRightToBottom' : 'leftToBottom'} width={isMobile ? 'lg' : 'max'} content={<TooltipCalSyncText t={t} />}>
                                <div className={CN.iconInfo}>
                                  <Icon variant="info" width="md" />
                                </div>
                              </Tooltip>
                              {t('stylistScreen.addStylist.pinCodeAcces.tooltipCalSync.title')}
                            </div>
                            <Toggler onClick={onCheckboxCalSync} selected={checkedCalSync} hideLabel />
                          </div>

                          {USE_CALLSYNC_BUTTON && checkedCalSync && (
                            <div>
                              <span className="field-name">{t('stylistScreen.addStylist.pinCodeAcces.calSyncEmailGuide')}</span>
                              <label htmlFor="calSyncEmail" style={{ position: 'relative' }}>
                                {emailHasError ? <span className="error">{t('common.errors.validation.notValidEmail')}</span> : null}
                                <input
                                  className="utl-full-width"
                                  id="calSyncEmail"
                                  type="text"
                                  placeholder=" "
                                  autoComplete="off"
                                  onChange={handleCalSyncEmailChange}
                                  onBlur={(e) => void validateCalSyncEmail(e.target.value)}
                                  value={inputEmailValue}
                                />
                                <button onClick={handleSendCalSyncEmail} className="input-with-button" disabled={emailHasError || !inputEmailValue} />
                              </label>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </section>
                {/* {transferPolicy && !servesAsWaitlist && ( */}
                <div data-bem="AddStylist__stripe-container" className={CN.stripeContainer}>
                  <div className={CN.stripeTitle}>
                    <img src={STRIPE_LOGO} alt="stripe logo" className={CN.stripeLogo} />
                    {t('stylistScreen.paymentsAccount')}
                  </div>
                  <div className={CN.stripeLabel}>
                    <div>{t('stylistScreen.addStylist.pinCodeAcces.accountType')}</div>
                    <div className={CN.stripeBadge}>
                      <Badge variant={transferPolicy === TRANSFER_POLICY_TEXT_LOWER.noTransfer ? 'warning' : 'fb'}>
                        <Icon variant="stripeWhite" width="xs" />
                        <div className={CN.badgeFont}>{t(getTransferPolicyText(transferPolicy))}</div>
                      </Badge>
                    </div>
                  </div>
                  <div className={CN.stripeButtonContainer}>
                    <ButtonModal variant="outline" onClick={handleClickStripe} xSpace>
                      {transferPolicy === TRANSFER_POLICY_TEXT_LOWER.noTransfer
                        ? t('stylistScreen.addStylist.pinCodeAcces.setupStripeButton')
                        : t('stylistScreen.addStylist.pinCodeAcces.manageAccountButton')}
                    </ButtonModal>
                  </div>
                </div>
                {/* )} */}
              </div>
            )}

            {/* WAITLIST CHECKBOX*/}
            <ResponsiveBox.Mobile>
              {(isInternal || stylistId) && serves_as_waitlist && hasColumn && (
                <div data-bem="AddStylist__waitlist-toggler">
                  <div className={CN.waitlistTogglerContainer}>
                    <span className={CN.waitlistTogglerLabel}>{t('stylistScreen.addStylist.waitlistTogglerLabel')}</span>
                    <Toggler onClick={setWaitList} selected={serves_as_waitlist} hideLabel />
                    <TooltipForReceptionist permission={PERM.canEditStaff} where={IN.inWaitlist} />
                  </div>
                  <div className={CN.waitlistDescription}>{t('stylistScreen.addStylist.waitlistDescription')}</div>
                </div>
              )}
            </ResponsiveBox.Mobile>
            {/*  WAITLIST RULES CARD */}
            {serves_as_waitlist && (
              <div
                className={cn({
                  [CN.cardWaitlistContainer]: true,
                  [CN.existingWaitlistSpace]: serves_as_waitlist,
                })}
              >
                <CardWaitlist tag="AddStylist__edit" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
